import type { SituacijaCheck, SituacijaReport } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { BadgeStatus, type StatusKind } from "@/components/ui/badge-status";
import { OriginalDocButton } from "./OriginalDocViewer";
import { situacijaExtraction } from "./situacijaStatus";
import { useIntakeStore } from "./useIntakeStore";

/** Status string (Croatian) → badge colour ramp. */
function kindFor(status: string): StatusKind {
  const s = status.toUpperCase();
  if (s === "OK") return "ok";
  if (s.startsWith("GRANI")) return "warn"; // GRANIČNO
  return "err"; // PREKORAČENO / anything else
}

function fmt(v: number | null): string {
  return v === null || v === undefined ? "—" : String(v).replace(".", ",");
}

/** The matica variables the situacija feeds into the opći dio, in render order. */
const MATICA_FIELDS: Array<[key: string, label: string]> = [
  ["footprint_area_m2", "Tlocrtna projekcija"],
  ["kig", "KIG"],
  ["kis", "KISN"],
  ["gross_floor_area", "GBP (ukupno)"],
];

function CheckRow({ c }: { c: SituacijaCheck }) {
  return (
    <li className="flex flex-col gap-1 border-b border-stone-100 py-2 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <BadgeStatus kind={kindFor(c.status)}>{c.status}</BadgeStatus>
        <span className="text-[0.85rem] font-semibold text-stone-800">
          {c.parameter}
        </span>
        <span className="ml-auto text-[0.74rem] text-stone-500">
          dekl. {fmt(c.declared)}
          {c.recomputed !== null && c.recomputed !== c.declared && (
            <> · izračun {fmt(c.recomputed)}</>
          )}
          {c.limit !== null && (
            <>
              {" "}
              · {c.limit_type === "min" ? "min" : "max"} {fmt(c.limit)}
            </>
          )}
        </span>
      </div>
      {c.note && <p className="text-[0.74rem] text-stone-500">{c.note}</p>}
    </li>
  );
}

/** Row-summary badge for the situacija state — the Donesi konzola shows it
 * on the "Situacija" row; honest read-state, never green over empty. */
export function SituacijaSummaryBadge({
  report,
}: {
  report: SituacijaReport | null;
}) {
  if (!report) {
    return (
      <span className="text-stone-400">vektorski PDF s iskazom površina</span>
    );
  }
  const extraction = situacijaExtraction(report);
  if (extraction.empty) {
    return <BadgeStatus kind="err">iskaz nije pročitan</BadgeStatus>;
  }
  if (extraction.missing.length) {
    return (
      <BadgeStatus kind="warn">
        {`pročitano ${extraction.parsed.length}/${
          extraction.parsed.length + extraction.missing.length
        } parametara`}
      </BadgeStatus>
    );
  }
  return (
    <BadgeStatus kind={report.flags.length ? "warn" : "ok"}>
      {report.flags.length
        ? `učitana · ${report.flags.length} za pregled`
        : "učitana"}
    </BadgeStatus>
  );
}

/**
 * The declared urbanistic iskaz of the vector-PDF *situacija*, parsed +
 * validated and routed into the canonical project model. Read-only by
 * design — the upload happens in the basket or on the console row; this is
 * the EVIDENCE the "Situacija" row expands to (its own card until F1). The
 * figures (footprint/KIG/KISN/GBP) feed the opći dio / tehnički opis.
 */
export function SituacijaDetails({ projectId }: { projectId: string }) {
  const setSituacija = useIntakeStore((s) => s.setSituacija);
  const setOpciDioStatus = useIntakeStore((s) => s.setOpciDioStatus);
  // Render from the shared store — the landing intake or the sources
  // basket can load a situacija before this panel ever mounts.
  const report = useIntakeStore((s) => s.get(projectId).situacija);
  const opciDioStatus = useIntakeStore((s) => s.get(projectId).opciDioStatus);
  // The situacija is parsed from the same file the recognizer persisted as a
  // geodetski elaborat — reach across to its doc_id so we can show the original.
  const recognizedDocs = useIntakeStore((s) => s.get(projectId).recognizedDocs);
  const situacijaDocId = recognizedDocs.find(
    (d) => d.document_type === "geodetski_elaborat" && d.doc_id,
  )?.doc_id;

  const variables = (opciDioStatus?.project_model.variables ?? {}) as Record<
    string,
    unknown
  >;
  const provenance = opciDioStatus?.project_model.provenance ?? {};

  // Honest read-state: never render success over an empty report.
  const extraction = report ? situacijaExtraction(report) : null;

  return (
    <>
        {!report && (
          <p className="text-[0.85rem] text-stone-500">
            Povuci vektorski PDF situacije na redak gore ili u koš ispod —
            sustav ga prepozna i ovdje raspiše urbanističke parametre
            (KIG / KISN / footprint / GBP / hortikultura / parking).
          </p>
        )}

        {report && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <BadgeStatus
                kind={
                  extraction!.empty
                    ? "err"
                    : extraction!.missing.length || report.flags.length
                      ? "warn"
                      : "ok"
                }
                title="Interna provjera deklariranih vrijednosti"
              >
                {extraction!.empty
                  ? "iskaz nije pročitan"
                  : report.flags.length
                    ? `${report.flags.length} za pregled`
                    : "provjereno"}
              </BadgeStatus>
              <span className="text-[0.78rem] text-stone-500">
                {report.file ?? "situacija.pdf"}
              </span>
              {situacijaDocId && (
                <OriginalDocButton docId={situacijaDocId} fileName={report.file} />
              )}
              <Button
                type="button"
                variant="ghost"
                className="ml-auto"
                onClick={() => {
                  setSituacija(projectId, null);
                  setOpciDioStatus(projectId, null);
                }}
              >
                Ukloni — učitaj drugu na ovom retku
              </Button>
            </div>

            {/* Honest diagnosis — say exactly what was (not) read and why,
                instead of a green badge over an empty report. */}
            {extraction!.empty && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.82rem] text-red-800">
                <p className="font-semibold">
                  Iz ovog PDF-a nije pročitan nijedan urbanistički parametar.
                </p>
                <p className="mt-1 text-red-700">
                  {extraction!.text_layer_chars === 0
                    ? "PDF nema tekstualni sloj (vjerojatno sken ili rasterski izvoz) — iskaz se ne može pročitati iz slike."
                    : "PDF ima tekstualni sloj, ali tablica iskaza (KIG / KISN / GBP / čestica) nije pronađena na listu ili je u formatu koji sustav još ne prepoznaje."}
                </p>
                <p className="mt-1 text-red-700">
                  Vrijednosti možeš unijeti ručno na koraku Provjeri — ili
                  učitaj list koji sadrži tablicu iskaza površina.
                </p>
              </div>
            )}
            {!extraction!.empty && extraction!.missing.length > 0 && (
              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-[0.8rem] text-amber-800">
                <span className="font-semibold">Pročitano:</span>{" "}
                {extraction!.parsed.join(", ")}
                {" · "}
                <span className="font-semibold">nije pronađeno:</span>{" "}
                {extraction!.missing.join(", ")}
              </div>
            )}

            {/* Matica variables — what the situacija feeds into the opći dio.
                Hidden until a status computed them (a landing-loaded situacija
                arrives without one until the completeness check runs). */}
            {MATICA_FIELDS.some(([k]) => variables[k] != null) && (
            <section>
              <h4 className="mb-1 text-[0.8rem] font-bold uppercase tracking-[0.04em] text-stone-600">
                Ulazi u dokumentaciju
              </h4>
              <p className="mb-1 text-[0.74rem] text-stone-400">
                Ove vrijednosti ulaze izravno u tekstualni dio dokumentacije —
                kasniji ručni unos ih ne može neprimijećeno promijeniti.
              </p>
              <ul className="rounded-md border border-stone-200 bg-white px-3">
                {MATICA_FIELDS.filter(([k]) => variables[k] != null).map(
                  ([key, label]) => {
                    const prov = provenance[key];
                    return (
                      <li
                        key={key}
                        className="flex items-center gap-2 border-b border-stone-100 py-2 last:border-0"
                      >
                        <span className="text-[0.85rem] text-stone-700">
                          {label}
                        </span>
                        <span className="text-[0.85rem] font-semibold text-stone-900">
                          {String(variables[key])}
                        </span>
                        {prov?.source && (
                          <span className="ml-auto text-[0.68rem] uppercase tracking-[0.04em] text-stone-400">
                            {prov.source}
                          </span>
                        )}
                      </li>
                    );
                  },
                )}
              </ul>
            </section>
            )}

            {/* Internal-consistency checks (the dvostruka provjera) —
                hidden when nothing was parsed (an empty list reads as
                "all good", which is the opposite of the truth). */}
            {report.checks.length > 0 && (
              <section className="mt-4">
                <h4 className="mb-1 text-[0.8rem] font-bold uppercase tracking-[0.04em] text-stone-600">
                  Provjera deklariranih vrijednosti
                </h4>
                <ul className="rounded-md border border-stone-200 bg-white px-3">
                  {report.checks.map((c, i) => (
                    <CheckRow key={`${c.parameter}-${i}`} c={c} />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
    </>
  );
}
